import React, { useEffect, useRef, useState, useContext } from "react";
import { createPortal } from "react-dom";
import CloseModal from "../../Atomos/Icons/EditMode/CloseModal";
import CancelIcon from "../../Atomos/Icons/Close";
import SaveChanges from "../../Atomos/Icons/EditMode/SaveChanges";
import {
  memberModalPrimaryButtonClassName,
  memberModalSecondaryButtonClassName,
} from "./memberModalFooterButtonClasses";
import { IPerson } from "../../../interfaces/models/IPerson";
import { uploadImage } from "../../../utils/UploadImage";
import { updatePerson } from "../../../Services/Person";
import { toast } from "react-toastify";
import {
  createProfilePhotoTimestamp,
  parseProfilePhotoTimestamp,
  resolveProfilePictureDisplayUrl,
} from "../../../utils/profilePictureUrl";
import { notifyProfilePhotoUpdated } from "../../../utils/notifyProfilePhotoUpdated";
import AuthContext from "../../../store/AuthContext";

const MODAL_PAD = "px-6 md:px-8";
const MAX_IMAGE_SIZE = 25 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".heif",
  ".bmp",
  ".tiff",
  ".dng",
  ".raw",
];

type UploadProfileImageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  person: IPerson;
  /** Current profile photo URL shown in the preview when no new file is selected. */
  initialPhotoUrl?: string | null;
  /** Called after save with the new `person.photo` timestamp. */
  onPhotoSaved: (photoTimestamp: string) => void;
};

const checkImageType = (ext: string | undefined) =>
  ACCEPTED_EXTENSIONS.includes(`.${ext?.toLowerCase() ?? ""}`);

const UploadProfileImageModal: React.FC<UploadProfileImageModalProps> = ({
  isOpen,
  onClose,
  person,
  initialPhotoUrl,
  onPhotoSaved,
}) => {
  const { loggedPerson } = useContext(AuthContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewObjectUrlRef = useRef<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const revokePreviewUrl = () => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setPreviewSrc(null);
      setLoadError(null);
      setSaving(false);
      revokePreviewUrl();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setPreviewSrc(
      person.id
        ? resolveProfilePictureDisplayUrl(person.id, person.photo, {
            width: 360,
            height: 360,
            photoUpdatedAt: person._ts,
          })
        : initialPhotoUrl?.trim() || null,
    );
  }, [isOpen, initialPhotoUrl, person.id, person.photo, person._ts]);

  useEffect(() => {
    return () => {
      revokePreviewUrl();
    };
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    setLoadError(null);

    const ext = file.name.split(".").pop();
    if (!checkImageType(ext)) {
      setLoadError("File type not supported.");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setLoadError("Image must be 25 MB or smaller.");
      return;
    }

    revokePreviewUrl();
    const objectUrl = URL.createObjectURL(file);
    previewObjectUrlRef.current = objectUrl;
    setSelectedFile(file);
    setPreviewSrc(objectUrl);
  };

  const handleSave = async () => {
    if (!person.id || !selectedFile) {
      toast.error("Choose an image before saving.");
      return;
    }
    setSaving(true);
    try {
      const uploaded = await uploadImage(
        selectedFile,
        person.id,
        "user/profile-pictures/",
      );
      if (!uploaded) {
        toast.error("Upload failed. Please try again.");
        return;
      }
      const parsed = parseProfilePhotoTimestamp(String(uploaded));
      const photoTimestamp =
        parsed != null ? String(parsed) : createProfilePhotoTimestamp();
      const updated = { ...person, photo: photoTimestamp };
      await updatePerson(updated);
      onPhotoSaved(photoTimestamp);
      if (loggedPerson?.id === person.id) {
        notifyProfilePhotoUpdated({ ...loggedPerson, ...updated, photo: photoTimestamp });
      }
      toast.success("Profile photo updated.");
      onClose();
    } catch {
      toast.error("Could not save your photo.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const card = (
    <div
      className="flex w-full max-w-lg flex-col overflow-hidden rounded-lg bg-white shadow-xl"
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-labelledby="upload-profile-image-title"
    >
      <div
        className={`flex shrink-0 items-start justify-between gap-4 ${MODAL_PAD} pt-6 md:pt-8`}
      >
        <h2
          id="upload-profile-image-title"
          className="font-decimal text-2xl font-semibold text-primary"
        >
          Upload Profile Image
        </h2>
        <button
          type="button"
          className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center text-gray-font hover:text-gray-600"
          onClick={onClose}
          disabled={saving}
          aria-label="Close"
        >
          <CloseModal />
        </button>
      </div>

      <div className={`${MODAL_PAD} pb-2 pt-4`}>
        {previewSrc ? (
          <div className="flex justify-center rounded-lg bg-[#F4F4F4] p-4">
            <img
              src={previewSrc}
              alt="Profile preview"
              className="max-h-[min(400px,60vh)] max-w-full object-contain"
            />
          </div>
        ) : (
          <div className="flex min-h-[280px] flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-8">
            <p className="text-center text-sm text-gray-font">
              Choose a photo to preview and upload.
            </p>
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-font hover:bg-gray-50"
              onClick={() => fileInputRef.current?.click()}
            >
              Select image
            </button>
          </div>
        )}

        {previewSrc && (
          <button
            type="button"
            className="mt-3 text-sm font-medium text-primary underline"
            onClick={() => fileInputRef.current?.click()}
          >
            Choose a different image
          </button>
        )}

        {loadError && (
          <p className="mt-3 text-sm text-feedback-error">{loadError}</p>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
        className="hidden"
        onChange={handleFileChange}
      />

      <div
        className={`flex flex-wrap justify-end gap-3 border-t border-gray-200 ${MODAL_PAD} py-5`}
      >
        <button
          type="button"
          className={memberModalSecondaryButtonClassName}
          onClick={onClose}
          disabled={saving}
        >
          <span>Cancel</span>
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-gray-font">
            <CancelIcon className="h-4 w-4" />
          </span>
        </button>
        <button
          type="button"
          className={memberModalPrimaryButtonClassName}
          onClick={handleSave}
          disabled={saving || !selectedFile}
        >
          <span>Save</span>
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-white [&_svg]:fill-current">
            <SaveChanges />
          </span>
        </button>
      </div>
    </div>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
      onClick={saving ? undefined : onClose}
      role="presentation"
    >
      {card}
    </div>,
    document.body,
  );
};

export default UploadProfileImageModal;
